import { useCallback, useMemo } from "react"

import {
  CRITERION_VALUE,
  type CriterionValue,
} from "@/constants/criterion-values"
import { useHumanFeedbackUpdate } from "@/human-feedback/annotations"
import { useUser } from "@clerk/nextjs"

import type { Annotation } from "@/types/api/Annotation"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import {
  buildCriterionUpdatePayload,
  getCompositeResultData,
  type CompositeEvaluationResultJson,
  type CriterionResult,
} from "../records/helpers/composite-evaluation-types"

interface UseCriterionVotingProps {
  metricId: string
  simulationOutputId: string
  annotationsForMetric: Annotation[]
  metricOutput?: RunMetricOutput
  /**
   * Criteria text from metric metadata, used as a fallback when the metric
   * output has no per-criterion `individual_results` (e.g. before any run has
   * produced results). When omitted, the criteria list is empty in that case.
   */
  metadataCriteria?: string[] | null
  /** Forwarded to {@link useHumanFeedbackUpdate} for review-overview cache updates. */
  projectId?: string
  reportingMethod?: string
  /**
   * When true, the computed float score and json summary are only persisted
   * once every criterion has been voted on; partial votes persist only the
   * set tracker. When false, every change persists the score immediately.
   */
  gateScoreUntilAllVoted?: boolean
}

interface UseCriterionVotingReturn {
  /** The criteria to render, derived from the metric output or metadata fallback. */
  criteria: CriterionResult[]
  /** The current user's annotation, after any local optimistic updates. */
  currentAnnotation: Annotation | null
  /** The per-criterion human values, or null when none recorded yet. */
  selectedValues: string[] | null
  /** Toggle/set the human vote for the criterion at `index`. */
  handleCriterionChange: (index: number, value: CriterionValue) => void
}

const isVotedValue = (value: string): boolean =>
  value === CRITERION_VALUE.MET ||
  value === CRITERION_VALUE.NOT_MET ||
  value === CRITERION_VALUE.UNKNOWN

/**
 * Shared criterion-voting state for composite-evaluation surfaces.
 *
 * Resolves the current user's annotation, derives the criteria list, and
 * exposes a vote handler that builds and persists the human-feedback payload.
 * Used by both the assignments sub-rows and the deep-dive expanded voting,
 * which differ only in their criteria fallback and score-gating behavior.
 */
export const useCriterionVoting = ({
  metricId,
  simulationOutputId,
  annotationsForMetric,
  metricOutput,
  metadataCriteria,
  projectId,
  reportingMethod,
  gateScoreUntilAllVoted = false,
}: UseCriterionVotingProps): UseCriterionVotingReturn => {
  const { user } = useUser()
  const userEmail = user?.emailAddresses?.[0]?.emailAddress

  const myAnnotation = useMemo(
    () =>
      annotationsForMetric?.find(
        (annotation) => annotation.assignee === userEmail,
      ) ?? null,
    [annotationsForMetric, userEmail],
  )

  const { currentAnnotation, handleValueUpdate } = useHumanFeedbackUpdate({
    simulationOutputId,
    metricId,
    initialAnnotation: myAnnotation,
    projectId,
  })

  const resultJson = getCompositeResultData(metricOutput)
  const criteria = useMemo<CriterionResult[]>(() => {
    if (resultJson?.individual_results?.length) {
      return resultJson.individual_results
    }
    if (metadataCriteria?.length) {
      return metadataCriteria.map((text) => ({
        criterion: text,
        result: "" as CriterionValue,
        explanation: "",
        matching_message_indices: [],
      }))
    }
    return []
  }, [resultJson?.individual_results, metadataCriteria])

  const resolvedReportingMethod =
    reportingMethod ??
    (metricOutput?.result_json as CompositeEvaluationResultJson | undefined)
      ?.reporting_method ??
    "percentage_of_criteria_met"

  const handleCriterionChange = useCallback(
    (index: number, value: CriterionValue) => {
      const { floatScore, setTracker, jsonValue } = buildCriterionUpdatePayload(
        index,
        value,
        currentAnnotation?.ground_truth_set_value,
        criteria,
        resolvedReportingMethod,
      )

      if (!gateScoreUntilAllVoted) {
        void handleValueUpdate(floatScore, null, setTracker, jsonValue)
        return
      }

      const allNowVoted =
        setTracker.length === criteria.length &&
        criteria.length > 0 &&
        setTracker.every(isVotedValue)
      void handleValueUpdate(
        allNowVoted ? floatScore : null,
        null,
        setTracker,
        allNowVoted ? jsonValue : undefined,
      )
    },
    [
      currentAnnotation?.ground_truth_set_value,
      criteria,
      resolvedReportingMethod,
      handleValueUpdate,
      gateScoreUntilAllVoted,
    ],
  )

  return {
    criteria,
    currentAnnotation,
    selectedValues: currentAnnotation?.ground_truth_set_value ?? null,
    handleCriterionChange,
  }
}
